import { useEffect, useState, useCallback } from 'react';
import { institutionUsers, institutionPFiles, institutionDepartments } from '@/services/institution';
import type { User, PFile, Department } from '@/services/institution';
import styles from './StaffPortalPage.module.css';

// Types for the service portal
interface StaffCareerHistory {
  serviceYears: number;
  serviceMonths: number;
  currentPosition: string;
  currentGrade: string;
  currentDepartment: string;
  appointmentDate: string;
  employeeId: string;
}

interface DocumentRequest {
  id: string;
  requestNumber: string;
  staffId: string;
  staffName: string;
  documentType: string;
  purpose: string;
  status: 'submitted' | 'processing' | 'ready' | 'collected';
  priority: 'normal' | 'urgent';
  requestedAt: Date;
  processedAt?: Date;
  completedAt?: Date;
}

// Mock document requests for demo
const generateMockRequests = (staffId: string, staffName: string): DocumentRequest[] => [
  {
    id: 'req_001',
    requestNumber: `DOC-${new Date().getFullYear()}-0045`,
    staffId,
    staffName,
    documentType: 'Employment Verification',
    purpose: 'Bank loan application',
    status: 'ready',
    priority: 'normal',
    requestedAt: new Date(Date.now() - 172800000),
    processedAt: new Date(Date.now() - 86400000),
    completedAt: new Date(Date.now() - 43200000),
  },
  {
    id: 'req_002',
    requestNumber: `DOC-${new Date().getFullYear()}-0067`,
    staffId,
    staffName,
    documentType: 'Service Certificate',
    purpose: 'Personal records',
    status: 'processing',
    priority: 'normal',
    requestedAt: new Date(Date.now() - 86400000),
  },
];

// Document types that can be generated
const DOCUMENT_TYPES = [
  { type: 'service_certificate', name: 'Certificate of Service', icon: '📜', fee: 20, description: 'Official certificate showing years of service' },
  { type: 'employment_verification', name: 'Employment Verification', icon: '✅', fee: 15, description: 'Letter confirming current employment status' },
  { type: 'salary_slip', name: 'Salary Slip', icon: '💰', fee: 5, description: 'Monthly salary statement' },
  { type: 'promotion_history', name: 'Promotion History', icon: '📈', fee: 15, description: 'Record of all promotions' },
  { type: 'leave_balance', name: 'Leave Balance Statement', icon: '🏖️', fee: 5, description: 'Current leave balance' },
  { type: 'good_standing', name: 'Letter of Good Standing', icon: '🎖️', fee: 25, description: 'Certification of good standing' },
];

export function StaffPortalPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [allStaff, setAllStaff] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<User | null>(null);
  const [staffPFile, setStaffPFile] = useState<PFile | null>(null);
  const [staffCareer, setStaffCareer] = useState<StaffCareerHistory | null>(null);
  const [documentRequests, setDocumentRequests] = useState<DocumentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'search' | 'profile' | 'documents' | 'requests'>('search');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');

  // Document generation state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [docPurpose, setDocPurpose] = useState('');
  const [docPriority, setDocPriority] = useState<'normal' | 'urgent'>('normal');

  // Load initial data
  useEffect(() => {
    async function loadInitialData() {
      setLoading(true);
      try {
        const [usersRes, deptsRes] = await Promise.all([
          institutionUsers.list({ perPage: 100 }),
          institutionDepartments.list(),
        ]);

        if (usersRes.success && usersRes.data) {
          setAllStaff(usersRes.data);
          setSearchResults(usersRes.data.slice(0, 10));
        }

        if (deptsRes.success && deptsRes.data) {
          setDepartments(deptsRes.data);
        }
      } catch (err) {
        console.error('Failed to load initial data:', err);
      } finally {
        setLoading(false);
      }
    }

    loadInitialData();
  }, []);

  // Search staff
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() && !selectedDepartment) {
      setSearchResults(allStaff.slice(0, 10));
      return;
    }

    setSearchLoading(true);
    try {
      const params: { search?: string; departmentId?: string; perPage: number } = { perPage: 50 };
      if (searchQuery.trim()) params.search = searchQuery;
      if (selectedDepartment) params.departmentId = selectedDepartment;

      const response = await institutionUsers.list(params);
      if (response.success && response.data) {
        setSearchResults(response.data);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, selectedDepartment, allStaff]);

  // Handle search on enter or button click
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.length >= 2 || selectedDepartment) {
        handleSearch();
      } else if (!searchQuery && !selectedDepartment) {
        setSearchResults(allStaff.slice(0, 10));
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedDepartment, handleSearch, allStaff]);

  // Select a staff member
  const handleSelectStaff = async (staff: User) => {
    setSelectedStaff(staff);
    setActiveTab('profile');

    // Load P-File and career history
    try {
      const pfileRes = await institutionPFiles.getByStaffId(staff.id);
      if (pfileRes.success && pfileRes.data) {
        setStaffPFile(pfileRes.data);
      } else {
        setStaffPFile(null);
      }
    } catch {
      setStaffPFile(null);
    }

    // Calculate career history from available data
    const appointmentDate = staff.createdAt;
    const startDate = new Date(appointmentDate);
    const now = new Date();
    const diffMonths = (now.getFullYear() - startDate.getFullYear()) * 12 + (now.getMonth() - startDate.getMonth());

    setStaffCareer({
      serviceYears: Math.floor(diffMonths / 12),
      serviceMonths: diffMonths % 12,
      currentPosition: staff.jobTitle || 'Not specified',
      currentGrade: `Clearance Level ${staff.securityClearance}`,
      currentDepartment: staff.department?.name || 'Not assigned',
      appointmentDate: staff.createdAt,
      employeeId: staff.employeeId || 'Not assigned',
    });

    // Load mock document requests
    setDocumentRequests(generateMockRequests(staff.id, `${staff.firstName} ${staff.lastName}`));
  };

  // Generate document
  const handleGenerateDocument = () => {
    if (!selectedStaff || !selectedDocType || !docPurpose) return;

    const docType = DOCUMENT_TYPES.find(d => d.type === selectedDocType);
    const newRequest: DocumentRequest = {
      id: `req_${Date.now()}`,
      requestNumber: `DOC-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
      staffId: selectedStaff.id,
      staffName: `${selectedStaff.firstName} ${selectedStaff.lastName}`,
      documentType: docType?.name || selectedDocType,
      purpose: docPurpose,
      status: 'submitted',
      priority: docPriority,
      requestedAt: new Date(),
    };

    setDocumentRequests([newRequest, ...documentRequests]);
    setShowGenerateModal(false);
    setSelectedDocType('');
    setDocPurpose('');
    setDocPriority('normal');
    setActiveTab('requests');
  };

  // Format date
  const formatDate = (date: string | Date): string => {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ready':
      case 'collected':
        return 'var(--success)';
      case 'processing':
        return 'var(--warning)';
      case 'submitted':
        return 'var(--info)';
      default:
        return 'var(--text-muted)';
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner} />
          <span>Loading Staff Service Portal...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerBadge}>Records Unit</div>
          <h1 className={styles.title}>Staff Service Portal</h1>
          <p className={styles.subtitle}>
            Search and manage staff records, generate documents, and process requests
          </p>
        </div>
        {selectedStaff && (
          <div className={styles.selectedStaffCard}>
            <div className={styles.staffAvatar}>
              {selectedStaff.firstName[0]}{selectedStaff.lastName[0]}
            </div>
            <div className={styles.staffInfo}>
              <span className={styles.staffName}>{selectedStaff.firstName} {selectedStaff.lastName}</span>
              <span className={styles.staffRole}>{selectedStaff.jobTitle || 'Staff Member'}</span>
            </div>
            <button className={styles.clearButton} onClick={() => { setSelectedStaff(null); setActiveTab('search'); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'search' ? styles.active : ''}`}
          onClick={() => setActiveTab('search')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <span>Search Staff</span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'profile' ? styles.active : ''}`}
          onClick={() => setActiveTab('profile')}
          disabled={!selectedStaff}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span>Staff Profile</span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'documents' ? styles.active : ''}`}
          onClick={() => setActiveTab('documents')}
          disabled={!selectedStaff}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <span>Generate Documents</span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'requests' ? styles.active : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <path d="M3 9h18M9 21V9" />
          </svg>
          <span>Request Tracking</span>
          {documentRequests.filter(r => r.status === 'ready').length > 0 && (
            <span className={styles.tabBadge}>{documentRequests.filter(r => r.status === 'ready').length}</span>
          )}
        </button>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className={styles.content}>
          <div className={styles.searchSection}>
            <h2 className={styles.sectionTitle}>Find Staff Member</h2>
            <p className={styles.sectionDesc}>Search by name, email, employee ID, or filter by department</p>

            <div className={styles.searchControls}>
              <div className={styles.searchInputWrapper}>
                <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search by name, email, or employee ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className={styles.clearSearchButton} onClick={() => setSearchQuery('')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              <select
                className={styles.departmentSelect}
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Search Results */}
          <div className={styles.resultsSection}>
            <div className={styles.resultsHeader}>
              <h3 className={styles.resultsTitle}>
                {searchLoading ? 'Searching...' : `${searchResults.length} Staff Found`}
              </h3>
            </div>

            {searchLoading ? (
              <div className={styles.resultsLoading}>
                <div className={styles.loadingSpinner} />
              </div>
            ) : searchResults.length > 0 ? (
              <div className={styles.staffGrid}>
                {searchResults.map((staff) => (
                  <div
                    key={staff.id}
                    className={`${styles.staffCard} ${selectedStaff?.id === staff.id ? styles.selected : ''}`}
                    onClick={() => handleSelectStaff(staff)}
                  >
                    <div className={styles.staffCardAvatar}>
                      {staff.firstName[0]}{staff.lastName[0]}
                    </div>
                    <div className={styles.staffCardContent}>
                      <span className={styles.staffCardName}>{staff.firstName} {staff.lastName}</span>
                      <span className={styles.staffCardTitle}>{staff.jobTitle || 'Staff Member'}</span>
                      <span className={styles.staffCardDept}>{staff.department?.name || 'No Department'}</span>
                    </div>
                    <div className={styles.staffCardMeta}>
                      <span className={styles.staffCardId}>{staff.employeeId || 'No ID'}</span>
                      <span className={`${styles.staffCardStatus} ${staff.isActive ? styles.active : styles.inactive}`}>
                        {staff.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className={styles.staffCardArrow}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyResults}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <h3>No Staff Found</h3>
                <p>Try adjusting your search criteria or select a different department</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && selectedStaff && (
        <div className={styles.content}>
          <div className={styles.profileSection}>
            {/* Profile Header */}
            <div className={styles.profileHeader}>
              <div className={styles.profileAvatarLarge}>
                {selectedStaff.firstName[0]}{selectedStaff.lastName[0]}
              </div>
              <div className={styles.profileHeaderInfo}>
                <h2 className={styles.profileName}>{selectedStaff.firstName} {selectedStaff.lastName}</h2>
                <p className={styles.profileTitle}>{selectedStaff.jobTitle || 'Staff Member'}</p>
                <div className={styles.profileBadges}>
                  <span className={`${styles.badge} ${selectedStaff.isActive ? styles.badgeSuccess : styles.badgeError}`}>
                    {selectedStaff.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className={styles.badge}>
                    Clearance Level {selectedStaff.securityClearance}
                  </span>
                </div>
              </div>
              <div className={styles.profileActions}>
                <button className={styles.primaryButton} onClick={() => setActiveTab('documents')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="12" y1="18" x2="12" y2="12" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                  </svg>
                  Generate Document
                </button>
              </div>
            </div>

            {/* Profile Details */}
            <div className={styles.profileGrid}>
              {/* Personal Information */}
              <div className={styles.profileCard}>
                <h3 className={styles.cardTitle}>Personal Information</h3>
                <div className={styles.infoList}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Email</span>
                    <span className={styles.infoValue}>{selectedStaff.email}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Phone</span>
                    <span className={styles.infoValue}>{selectedStaff.phone || 'Not provided'}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Employee ID</span>
                    <span className={styles.infoValue}>{selectedStaff.employeeId || 'Not assigned'}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Department</span>
                    <span className={styles.infoValue}>{selectedStaff.department?.name || 'Not assigned'}</span>
                  </div>
                </div>
              </div>

              {/* Career Summary */}
              {staffCareer && (
                <div className={styles.profileCard}>
                  <h3 className={styles.cardTitle}>Career Summary</h3>
                  <div className={styles.careerStats}>
                    <div className={styles.careerStat}>
                      <span className={styles.careerStatValue}>
                        {staffCareer.serviceYears}y {staffCareer.serviceMonths}m
                      </span>
                      <span className={styles.careerStatLabel}>Service Period</span>
                    </div>
                    <div className={styles.careerStat}>
                      <span className={styles.careerStatValue}>{staffCareer.currentGrade}</span>
                      <span className={styles.careerStatLabel}>Current Grade</span>
                    </div>
                  </div>
                  <div className={styles.infoList}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Current Position</span>
                      <span className={styles.infoValue}>{staffCareer.currentPosition}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Department</span>
                      <span className={styles.infoValue}>{staffCareer.currentDepartment}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Appointment Date</span>
                      <span className={styles.infoValue}>{formatDate(staffCareer.appointmentDate)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* P-File Information */}
              <div className={styles.profileCard}>
                <h3 className={styles.cardTitle}>Personnel File (P-File)</h3>
                {staffPFile ? (
                  <div className={styles.pfileInfo}>
                    <div className={styles.pfileHeader}>
                      <span className={styles.pfileNumber}>{staffPFile.fileNumber}</span>
                      <span className={`${styles.pfileStatus} ${styles[staffPFile.status]}`}>
                        {staffPFile.status}
                      </span>
                    </div>
                    <div className={styles.infoList}>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Location</span>
                        <span className={styles.infoValue}>
                          {staffPFile.physicalLocation || 'Not specified'}
                        </span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Documents</span>
                        <span className={styles.infoValue}>{staffPFile.documentCount} files</span>
                      </div>
                      {staffPFile.isCheckedOut && (
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Checked Out To</span>
                          <span className={styles.infoValue}>
                            {staffPFile.checkedOutToUser?.firstName} {staffPFile.checkedOutToUser?.lastName}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={styles.noPfile}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    <p>No P-File created for this staff member</p>
                    <button className={styles.secondaryButton}>Create P-File</button>
                  </div>
                )}
              </div>

              {/* Roles */}
              <div className={styles.profileCard}>
                <h3 className={styles.cardTitle}>Assigned Roles</h3>
                {selectedStaff.roles.length > 0 ? (
                  <div className={styles.rolesList}>
                    {selectedStaff.roles.map((role) => (
                      <div key={role.id} className={styles.roleItem}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        <span>{role.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={styles.emptyText}>No roles assigned</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && selectedStaff && (
        <div className={styles.content}>
          <div className={styles.documentsSection}>
            <div className={styles.documentsHeader}>
              <h2 className={styles.sectionTitle}>Generate Document for {selectedStaff.firstName} {selectedStaff.lastName}</h2>
              <p className={styles.sectionDesc}>Select a document type to generate an official document</p>
            </div>

            <div className={styles.documentTypeGrid}>
              {DOCUMENT_TYPES.map((doc) => (
                <div
                  key={doc.type}
                  className={`${styles.documentTypeCard} ${selectedDocType === doc.type ? styles.selected : ''}`}
                  onClick={() => { setSelectedDocType(doc.type); setShowGenerateModal(true); }}
                >
                  <span className={styles.docIcon}>{doc.icon}</span>
                  <span className={styles.docName}>{doc.name}</span>
                  <span className={styles.docDesc}>{doc.description}</span>
                  <span className={styles.docFee}>GH₵ {doc.fee}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className={styles.content}>
          <div className={styles.requestsSection}>
            <div className={styles.requestsHeader}>
              <h2 className={styles.sectionTitle}>Document Requests</h2>
              <p className={styles.sectionDesc}>Track and manage document generation requests</p>
            </div>

            {documentRequests.length > 0 ? (
              <div className={styles.requestsList}>
                {documentRequests.map((request) => (
                  <div key={request.id} className={styles.requestCard}>
                    <div className={styles.requestCardHeader}>
                      <div className={styles.requestInfo}>
                        <span className={styles.requestNumber}>{request.requestNumber}</span>
                        <span
                          className={styles.requestStatus}
                          style={{ backgroundColor: `${getStatusColor(request.status)}20`, color: getStatusColor(request.status) }}
                        >
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                        {request.priority === 'urgent' && (
                          <span className={styles.urgentBadge}>Urgent</span>
                        )}
                      </div>
                      <span className={styles.requestDate}>{formatDate(request.requestedAt)}</span>
                    </div>

                    <div className={styles.requestDetails}>
                      <div className={styles.requestDetail}>
                        <span className={styles.requestLabel}>Staff</span>
                        <span className={styles.requestValue}>{request.staffName}</span>
                      </div>
                      <div className={styles.requestDetail}>
                        <span className={styles.requestLabel}>Document Type</span>
                        <span className={styles.requestValue}>{request.documentType}</span>
                      </div>
                      <div className={styles.requestDetail}>
                        <span className={styles.requestLabel}>Purpose</span>
                        <span className={styles.requestValue}>{request.purpose}</span>
                      </div>
                    </div>

                    {/* Progress Timeline */}
                    <div className={styles.progressTimeline}>
                      {['submitted', 'processing', 'ready', 'collected'].map((step, index) => {
                        const steps = ['submitted', 'processing', 'ready', 'collected'];
                        const currentIndex = steps.indexOf(request.status);
                        const isComplete = index <= currentIndex;
                        const isCurrent = index === currentIndex;

                        return (
                          <div key={step} className={`${styles.progressStep} ${isComplete ? styles.complete : ''} ${isCurrent ? styles.current : ''}`}>
                            <div className={styles.progressDot} />
                            <span className={styles.progressLabel}>
                              {step.charAt(0).toUpperCase() + step.slice(1)}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {request.status === 'ready' && (
                      <div className={styles.requestActions}>
                        <button className={styles.primaryButton}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          Download Document
                        </button>
                        <button className={styles.secondaryButton}>Mark as Collected</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyRequests}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <path d="M3 9h18M9 21V9" />
                </svg>
                <h3>No Requests Yet</h3>
                <p>Document requests will appear here once created</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Generate Document Modal */}
      {showGenerateModal && selectedStaff && (
        <div className={styles.modalOverlay} onClick={() => setShowGenerateModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Generate Document</h2>
              <button className={styles.closeButton} onClick={() => setShowGenerateModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalStaffInfo}>
                <div className={styles.staffAvatar}>
                  {selectedStaff.firstName[0]}{selectedStaff.lastName[0]}
                </div>
                <div>
                  <span className={styles.staffName}>{selectedStaff.firstName} {selectedStaff.lastName}</span>
                  <span className={styles.staffRole}>{selectedStaff.employeeId || 'Staff Member'}</span>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Document Type</label>
                <select
                  className={styles.formSelect}
                  value={selectedDocType}
                  onChange={(e) => setSelectedDocType(e.target.value)}
                >
                  <option value="">Select document type</option>
                  {DOCUMENT_TYPES.map((doc) => (
                    <option key={doc.type} value={doc.type}>{doc.name} - GH₵ {doc.fee}</option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Purpose *</label>
                <textarea
                  className={styles.formTextarea}
                  value={docPurpose}
                  onChange={(e) => setDocPurpose(e.target.value)}
                  placeholder="Why is this document being requested?"
                  rows={3}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Priority</label>
                <div className={styles.priorityOptions}>
                  <label className={`${styles.priorityOption} ${docPriority === 'normal' ? styles.selected : ''}`}>
                    <input
                      type="radio"
                      name="priority"
                      value="normal"
                      checked={docPriority === 'normal'}
                      onChange={(e) => setDocPriority(e.target.value as 'normal' | 'urgent')}
                    />
                    <span className={styles.priorityLabel}>Normal</span>
                    <span className={styles.priorityDesc}>3-5 working days</span>
                  </label>
                  <label className={`${styles.priorityOption} ${docPriority === 'urgent' ? styles.selected : ''}`}>
                    <input
                      type="radio"
                      name="priority"
                      value="urgent"
                      checked={docPriority === 'urgent'}
                      onChange={(e) => setDocPriority(e.target.value as 'normal' | 'urgent')}
                    />
                    <span className={styles.priorityLabel}>Urgent</span>
                    <span className={styles.priorityDesc}>1-2 working days (+100% fee)</span>
                  </label>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.secondaryButton} onClick={() => setShowGenerateModal(false)}>
                Cancel
              </button>
              <button
                className={styles.primaryButton}
                onClick={handleGenerateDocument}
                disabled={!selectedDocType || !docPurpose.trim()}
              >
                Generate Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
